// src/hooks/useSampleOrdersData.js
import { useState, useEffect, useCallback, useRef } from "react";
import { getAuth } from "firebase/auth";
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
} from "firebase/firestore";
import { filterKitsForUser } from "../utils/sampleKitPermissions";

const safeSeconds = (ts) => {
  if (!ts) return 0;
  if (typeof ts.seconds === "number") return ts.seconds;
  return 0;
};

const normalizeRole = (role) => String(role || "").trim().toLowerCase();

const chunk = (arr, size) => {
  const out = [];
  const a = Array.isArray(arr) ? arr : [];
  for (let i = 0; i < a.length; i += size) out.push(a.slice(i, i + size));
  return out;
};

const safeArray = (v) => (Array.isArray(v) ? v : []);

const isStaffRole = (role) => {
  const r = normalizeRole(role);
  return r === "admin" || r === "owner" || r === "enduser" || r === "end_user";
};

const isOwnerOrAdminRole = (role) => {
  const r = normalizeRole(role);
  return r === "admin" || r === "owner";
};

const uniqStrings = (values) =>
  Array.from(
    new Set(
      safeArray(values)
        .filter(Boolean)
        .map((value) => String(value).trim())
        .filter(Boolean)
    )
  );

const getUserTerritoryIds = (profile) =>
  uniqStrings([...(safeArray(profile?.territoryIds)), ...(safeArray(profile?.territories))]);

// ✅ Progressive paging config (Admin/Owner only)
const SAMPLE_ORDERS_PAGE_SIZE = 250;
// If true, Admin/Owner gets first page immediately, then older pages stream in.
const AUTO_LOAD_REMAINING_ADMIN_PAGES = true;

export const useSampleOrdersData = (companyId, userProfile = null) => {
  const db = getFirestore();
  const auth = getAuth();
  const authUid = auth.currentUser?.uid || null;

  // ✅ Always have a "me" profile even if parent forgot to pass it
  const [me, setMe] = useState(userProfile);

  const [inventory, setInventory] = useState([]);
  const [sampleOrders, setSampleOrders] = useState([]);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [companySettings, setCompanySettings] = useState(null);
  const [kits, setKits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // ✅ Paging state (additive; won’t break existing callers)
  const [sampleOrdersHasMore, setSampleOrdersHasMore] = useState(false);
  const [sampleOrdersLoadingMore, setSampleOrdersLoadingMore] = useState(false);

  // Used to cancel background page loads when reloadAll is called again
  const sampleOrdersRunRef = useRef(0);

  // Keep me synced with prop if provided later
  useEffect(() => {
    if (userProfile) setMe(userProfile);
  }, [userProfile]);

  // Load my profile if not provided
  useEffect(() => {
    if (me) return;
    const uid = authUid;
    if (!uid) return;

    const run = async () => {
      try {
        const snap = await getDoc(doc(db, "users", uid));
        setMe(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      } catch (e) {
        console.warn("[useSampleOrdersData] Failed to load my profile:", e);
        setMe(null);
      }
    };

    run();
  }, [me, authUid, db]);

  const loadCompanySettings = useCallback(async () => {
    if (!companyId) return null;

    try {
      const companyRef = doc(db, "companies", companyId);
      const snap = await getDoc(companyRef);
      const data = snap.exists() ? snap.data() : null;
      setCompanySettings(data);
      return data;
    } catch (err) {
      console.error("Error loading company settings:", err);
      setCompanySettings(null);
      return null;
    }
  }, [db, companyId]);

  /**
   * ✅ Matches your AccountsTable behavior:
   * - Admin/Owner: load all accounts
   * - EndUser: load accounts via:
   *    1) visibility == "all" (optional legacy)
   *    2) groupIds array-contains-any chunks (10 max)
   */
  const loadAccounts = useCallback(
    async (profile = null) => {
      if (!companyId) return;

      try {
        const colRef = collection(db, "companies", companyId, "accounts");

        // ✅ FIX (minimal): if caller forgets to pass profile, fall back to `me`
        const effectiveProfile = profile || me;

        const role = normalizeRole(effectiveProfile?.role);
        const isAdmin = role === "admin" || role === "owner";

        if (isAdmin) {
          const snap = await getDocs(colRef);
          const list = snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
          setAccounts(list);
          return;
        }

        if (role === "customer") {
          const myAccountId = effectiveProfile?.accountId ? String(effectiveProfile.accountId) : "";
          if (!myAccountId) {
            setAccounts([]);
            return;
          }

          const accountSnap = await getDoc(doc(db, "companies", companyId, "accounts", myAccountId));
          setAccounts(accountSnap.exists() ? [{ id: accountSnap.id, ...accountSnap.data() }] : []);
          return;
        }

        const myUid = authUid || effectiveProfile?.uid || effectiveProfile?.id || null;
        const myGroupIds = safeArray(effectiveProfile?.groupIds).filter(Boolean).map(String);
        const myTerritoryIds = getUserTerritoryIds(effectiveProfile);

        const map = new Map();
        const addSnap = (snap) =>
          snap.docs.forEach((d) => map.set(d.id, { id: d.id, ...d.data() }));

        // Optional legacy: visibility == "all"
        try {
          const qAll = query(colRef, where("visibility", "==", "all"));
          const sAll = await getDocs(qAll);
          addSnap(sAll);
        } catch (e) {
          // ignore
        }

        if (myUid) {
          try {
            addSnap(await getDocs(query(colRef, where("assignedToId", "==", myUid))));
          } catch {}
          try {
            addSnap(await getDocs(query(colRef, where("createdBy", "==", myUid))));
          } catch {}
          try {
            addSnap(await getDocs(query(colRef, where("sharedWithUserIds", "array-contains", myUid))));
          } catch {}
          try {
            addSnap(await getDocs(query(colRef, where("visibleToUserIds", "array-contains", myUid))));
          } catch {}
        }

        for (const c of chunk(myGroupIds, 10)) {
          if (!c.length) continue;
          try {
            addSnap(await getDocs(query(colRef, where("groupIds", "array-contains-any", c))));
          } catch {}
        }

        for (const c of chunk(myTerritoryIds, 10)) {
          if (!c.length) continue;
          try {
            addSnap(await getDocs(query(colRef, where("territoryId", "in", c))));
          } catch {}
        }

        const list = Array.from(map.values()).sort((a, b) =>
          (a.name || "").localeCompare(b.name || "")
        );

        console.log("[useSampleOrdersData] Loaded scoped accounts:", {
          uid: myUid,
          groupCount: myGroupIds.length,
          territoryCount: myTerritoryIds.length,
          accountCount: list.length,
        });

        setAccounts(list);
      } catch (err) {
        console.error("Error loading accounts:", err);
        setAccounts([]);
      }
    },
    [authUid, db, companyId, me]
  );

  const loadInventory = useCallback(async () => {
    if (!companyId) return;

    try {
      const colRef = collection(db, "companies", companyId, "inventory");
      const snap = await getDocs(colRef);
      const list = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((x) => x.sampleable === true)
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setInventory(list);
    } catch (err) {
      console.error("Error loading inventory for samples:", err);
      setInventory([]);
    }
  }, [db, companyId]);

  /**
   * ✅ SAFE progressive loading:
   * - EndUser/Customer behavior unchanged (still loads as before)
   * - Admin/Owner: loads first page fast, then streams older pages in background
   *   (does NOT block reloadAll from finishing)
   */
  const loadSampleOrders = useCallback(async () => {
    if (!companyId) return;

    const role = normalizeRole(me?.role);
    const isAdmin = isOwnerOrAdminRole(role);

    // Cancel any previous background runs
    const runId = (sampleOrdersRunRef.current || 0) + 1;
    sampleOrdersRunRef.current = runId;

    setSampleOrdersHasMore(false);
    setSampleOrdersLoadingMore(false);

    try {
      const colRef = collection(db, "companies", companyId, "sampleOrders");

      if (!isAdmin) {
        const myUid = authUid || me?.uid || me?.id || null;
        const visibleMap = new Map();
        const addSnap = (snap) =>
          snap.docs.forEach((d) => visibleMap.set(d.id, { id: d.id, ...d.data() }));

        if (myUid) {
          try {
            addSnap(await getDocs(query(colRef, where("createdBy", "==", myUid))));
          } catch {}
          try {
            addSnap(await getDocs(query(colRef, where("assignedToId", "==", myUid))));
          } catch {}
          try {
            addSnap(await getDocs(query(colRef, where("assignedUserId", "==", myUid))));
          } catch {}
        }

        const accountIds = new Set();
        const accountsColRef = collection(db, "companies", companyId, "accounts");

        if (myUid) {
          try {
            const assignedAccounts = await getDocs(query(accountsColRef, where("assignedToId", "==", myUid)));
            assignedAccounts.docs.forEach((d) => accountIds.add(d.id));
          } catch {}
          try {
            const assignedUserAccounts = await getDocs(query(accountsColRef, where("assignedUserId", "==", myUid)));
            assignedUserAccounts.docs.forEach((d) => accountIds.add(d.id));
          } catch {}
        }

        const customerAccountIds = uniqStrings([
          ...(safeArray(me?.customerAccountIds)),
          ...(safeArray(me?.activeCustomerMembership?.accountIds)),
          me?.activeCustomerMembership?.primaryAccountId,
          me?.activeCustomerMembership?.accountId,
          me?.accountId,
        ]);
        customerAccountIds.forEach((id) => accountIds.add(id));

        for (const ids of chunk(Array.from(accountIds), 10)) {
          if (!ids.length) continue;
          try {
            addSnap(await getDocs(query(colRef, where("accountId", "in", ids))));
          } catch {}
          try {
            addSnap(await getDocs(query(colRef, where("locationId", "in", ids))));
          } catch {}
          try {
            addSnap(await getDocs(query(colRef, where("accountIds", "array-contains-any", ids))));
          } catch {}
        }

        const list = Array.from(visibleMap.values()).sort(
          (a, b) => safeSeconds(b.createdAt) - safeSeconds(a.createdAt)
        );
        setSampleOrders(list);
        return;
      }

      // ✅ Admin/Owner: first page fast
      const firstQ = query(
        colRef,
        orderBy("createdAt", "desc"),
        limit(SAMPLE_ORDERS_PAGE_SIZE)
      );

      const firstSnap = await getDocs(firstQ);

      // If a newer load started, stop
      if (sampleOrdersRunRef.current !== runId) return;

      const firstList = firstSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setSampleOrders(firstList);

      const lastDoc = firstSnap.docs.length
        ? firstSnap.docs[firstSnap.docs.length - 1]
        : null;

      const hasMore = firstSnap.docs.length === SAMPLE_ORDERS_PAGE_SIZE;
      setSampleOrdersHasMore(!!hasMore);

      // ✅ If you don’t want background loads yet, turn this off at top
      if (!AUTO_LOAD_REMAINING_ADMIN_PAGES || !hasMore || !lastDoc) return;

      // ✅ Background load older pages (doesn't block screen rendering)
      setSampleOrdersLoadingMore(true);

      (async () => {
        let cursor = lastDoc;

        while (cursor && sampleOrdersRunRef.current === runId) {
          try {
            const nextQ = query(
              colRef,
              orderBy("createdAt", "desc"),
              startAfter(cursor),
              limit(SAMPLE_ORDERS_PAGE_SIZE)
            );

            const nextSnap = await getDocs(nextQ);

            if (sampleOrdersRunRef.current !== runId) return;

            const nextDocs = nextSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

            if (!nextDocs.length) {
              setSampleOrdersHasMore(false);
              setSampleOrdersLoadingMore(false);
              return;
            }

            // Append in descending order (older pages come after newer)
            setSampleOrders((prev) => {
              const map = new Map();
              (prev || []).forEach((x) => map.set(x.id, x));
              nextDocs.forEach((x) => map.set(x.id, x));
              return Array.from(map.values()).sort(
                (a, b) => safeSeconds(b.createdAt) - safeSeconds(a.createdAt)
              );
            });

            cursor =
              nextSnap.docs.length ? nextSnap.docs[nextSnap.docs.length - 1] : null;

            const stillMore = nextSnap.docs.length === SAMPLE_ORDERS_PAGE_SIZE;
            setSampleOrdersHasMore(!!stillMore);

            if (!stillMore) {
              setSampleOrdersLoadingMore(false);
              return;
            }

            // Yield so UI stays responsive
            await new Promise((r) => setTimeout(r, 0));
          } catch (e) {
            console.warn("[useSampleOrdersData] Background paging failed:", e);
            setSampleOrdersLoadingMore(false);
            // Don’t wipe data; just stop paging
            return;
          }
        }

        setSampleOrdersLoadingMore(false);
      })();
    } catch (err) {
      if (err?.code === "permission-denied") {
        console.warn("[useSampleOrdersData] Sample orders unavailable for this user.");
      } else {
        console.error("Error loading sample orders:", err);
      }
      setSampleOrders([]);
      setSampleOrdersHasMore(false);
      setSampleOrdersLoadingMore(false);
    }
  }, [db, companyId, me, authUid]);

  const loadCompanyUsers = useCallback(async () => {
    if (!companyId) return;

    try {
      const usersRef = collection(db, "users");
      const qUsers = query(usersRef, where("companyId", "==", companyId));
      const snap = await getDocs(qUsers);

      const list = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((u) => isStaffRole(u.role))
        .sort((a, b) => (a.fullName || "").localeCompare(b.fullName || ""));

      setCompanyUsers(list);
    } catch (err) {
      console.error("Error loading company users:", err);
      setCompanyUsers([]);
    }
  }, [db, companyId]);

  const loadKits = useCallback(async () => {
    if (!companyId) return;

    try {
      const kitSnap = await getDocs(collection(db, "companies", companyId, "sampleKits"));
      const rawList = kitSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => safeSeconds(b.updatedAt) - safeSeconds(a.updatedAt));

      const list = filterKitsForUser(rawList, me);
      setKits(list);
    } catch (err) {
      console.error("Error loading sample kits:", err);
      setError("Failed to load sample kits.");
      setKits([]);
    }
  }, [db, companyId, me]);

  const reloadAll = useCallback(async () => {
    if (!companyId) return;

    setLoading(true);
    try {
      await loadCompanySettings();

      await Promise.all([
        loadInventory(),
        loadSampleOrders(), // ✅ now returns fast for Admin and streams older pages
        loadCompanyUsers(),
        loadAccounts(me),
        loadKits(),
      ]);
    } catch (err) {
      console.error("Error reloading data:", err);
      setError("Failed to reload data.");
    } finally {
      setLoading(false);
    }
  }, [
    companyId,
    me,
    loadCompanySettings,
    loadInventory,
    loadSampleOrders,
    loadCompanyUsers,
    loadAccounts,
    loadKits,
  ]);

  // Reload when companyId or me becomes available
  useEffect(() => {
    if (companyId) reloadAll();
  }, [companyId, me, reloadAll]);

  return {
    me,
    inventory,
    sampleOrders,
    companyUsers,
    accounts,
    companySettings,
    kits,
    loading,
    error,
    setError,
    reloadAll,
    loadInventory,
    loadSampleOrders,
    loadAccounts,
    loadKits,
    setSampleOrders,
    setAccounts,
    setKits,

    // ✅ Additive paging signals (optional to use later)
    sampleOrdersHasMore,
    sampleOrdersLoadingMore,
    sampleOrdersPageSize: SAMPLE_ORDERS_PAGE_SIZE,
  };
};
